/**
 * Configuración centralizada para la integración con Payku
 */

// Determinar entorno (desarrollo o producción)
const isDevelopment = typeof window !== 'undefined' && window.location.hostname === 'localhost';

// Configuración base
const paykuConfig = {
  // API base URL
  baseUrl: import.meta.env.VITE_PAYKU_API_URL || 'https://api.payku.cl/api/transaction',

  // Tokens (cargar desde variables de entorno)
  publicToken: import.meta.env.VITE_PAYKU_PUBLIC_TOKEN || '',
  privateToken: import.meta.env.VITE_PAYKU_PRIVATE_TOKEN || '',

  // URLs de la aplicación (se calcularán dinámicamente cuando se necesiten)
  getAppBaseUrl: () => {
    if (typeof window === 'undefined') return 'https://vendox.ai';
    return isDevelopment
      ? window.location.origin
      : 'https://vendox.ai'; // Cambiar a tu dominio de producción
  },

  // URLs de redirección
  getRedirectUrls: (transactionId?: string) => {
    const baseUrl = paykuConfig.getAppBaseUrl();
    return {
      returnUrl: `${baseUrl}/payment/return?source=payku${transactionId ? `&txid=${transactionId}` : ''}`,
      cancelUrl: `${baseUrl}/account`,
      notifyUrl: `${baseUrl}/api/payment/notify`
    };
  },

  // Configuración de transacciones
  transaction: {
    timeout: 30, // Timeout en minutos
    retryAttempts: 3, // Número de intentos para verificación
    retryDelayMs: 2000, // Tiempo base entre reintentos (ms)
    verificationTimeoutMs: 300000, // Timeout para verificación automática (5 minutos)
  },

  // Definición de paquetes disponibles (precios base en USD)
  // Los precios en CLP se calculan automáticamente con la tasa de conversión
  packages: [
    {
      id: 'basic',
      name: 'Starter',
      description: '1 Coin para generar 1 CV optimizado',
      amount_usd: 4.99, // $4.990 CLP ÷ 865
      amount_clp: 4990,
      vcoins: 1,
      popular: false
    },
    {
      id: 'standard',
      name: 'Paquete Estándar',
      description: '3 Coins para generar 3 CVs optimizados',
      amount_usd: 8.99, // $7.490 CLP ÷ 865 ≈ 8.66, redondeado a 8.99
      amount_clp: 7490,
      vcoins: 3,
      popular: false
    },
    {
      id: 'premium',
      name: 'Advanced',
      description: '10 Coins para generar 10 CVs optimizados - MEJOR OFERTA',
      amount_usd: 11.99, // $9.990 CLP ÷ 865 ≈ 11.55, redondeado a 11.99
      amount_clp: 9990,
      vcoins: 10,
      popular: true // Marcar como más popular
    }
  ],

  // Funciones de utilidad
  utils: {
    // Obtener detalles de un paquete por ID
    getPackageById: (packageId: string) => {
      return paykuConfig.packages.find(pkg => pkg.id === packageId);
    },

    // Calcular monedas para un monto personalizado
    calculateCoinsForCustomAmount: (amount: number): number => {
      // Buscar si coincide con un paquete predefinido (comparando con CLP)
      const foundPackage = paykuConfig.packages.find(pkg => pkg.amount_clp === amount);
      if (foundPackage) return foundPackage.vcoins;

      // Para montos personalizados, calcular proporcionalmente
      // con un pequeño bonus si el monto es mayor
      if (amount >= 200) return Math.floor(amount * 13); // 30% bonus
      if (amount >= 100) return Math.floor(amount * 12); // 20% bonus
      if (amount >= 50) return Math.floor(amount * 11);  // 10% bonus
      return amount * 10; // Sin bonus para montos pequeños
    },

    // Guardar información de transacción en almacenamiento local
    saveTransactionData: (transactionId: string, packageId: string, amount: number, vcoinsAmount: number) => {
      // Guardar en diferentes formatos para compatibilidad con diferentes partes del sistema

      // 1. Formato simple (ID de transacción)
      localStorage.setItem('last_payku_transaction', transactionId);
      localStorage.setItem('payku_current_transaction', transactionId);
      sessionStorage.setItem('payku_transaction_to_verify', transactionId);

      // 2. Timestamp para control de tiempo
      localStorage.setItem('last_payku_time', new Date().toISOString());

      // 3. Detalles completos en formato documentación
      localStorage.setItem('last_payku_details', JSON.stringify({
        id: transactionId,
        pack: packageId,
        amount: amount,
        vcoins: vcoinsAmount,
        timestamp: new Date().toISOString(),
        order_id: transactionId
      }));

      // 4. Formato para compatibilidad con sistema existente
      localStorage.setItem('payku_transaction', JSON.stringify({
        transaction_id: transactionId,
        package_id: packageId,
        amount: amount,
        vcoins_amount: vcoinsAmount,
        created_at: new Date().toISOString(),
        status: 'pending'
      }));

      // 5. Guardar pendingTransaction para compatibilidad
      const pendingTxData = {
        id: transactionId,
        transaction_id: transactionId,
        package_id: packageId,
        amount: amount,
        coins: vcoinsAmount,
        timestamp: Date.now()
      };
      localStorage.setItem('pendingTransaction', JSON.stringify(pendingTxData));
      sessionStorage.setItem('pendingTransaction', JSON.stringify(pendingTxData));

      // 6. Guardar configuración para verificación directa (solo en desarrollo)
      if (isDevelopment) {
        localStorage.setItem('payku_config', JSON.stringify({
          baseUrl: paykuConfig.baseUrl,
          publicToken: paykuConfig.publicToken
        }));
      }
    },

    // Limpiar datos de transacción después de verificación exitosa
    clearTransactionData: (transactionId: string) => {
      // Mantener algunos datos para referencia pero marcar como completados
      localStorage.setItem('last_verified_transaction', transactionId);
      localStorage.setItem('last_verification_timestamp', Date.now().toString());
      localStorage.setItem('verification_success', 'true');

      // Eliminar datos de transacción pendiente
      localStorage.removeItem('pendingTransaction');
      sessionStorage.removeItem('pendingTransaction');
      localStorage.removeItem('payku_current_transaction');
      sessionStorage.removeItem('payku_transaction_to_verify');
    }
  }
};

// Exportar configuración
export default paykuConfig; 